import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { 
  Presentation, 
  Plus, 
  Calendar,
  CheckCircle,
  Clock,
  Eye,
  Play,
  Search,
  Trophy,
  TrendingUp,
  Award,
  Target,
  Lightbulb,
  Sparkles,
  Loader2,
  ChevronRight,
  BarChart3,
  Medal,
  Building,
  Users,
  Star,
  User,
  ChevronsUpDown,
  Check
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Progress } from "../components/ui/progress";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function UserDashboard() {
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [allSessions, setAllSessions] = useState([]);
  const [selectedUser, setSelectedUser] = useState("");
  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [viewMode, setViewMode] = useState("user"); // "user" or "department"
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [leaderboard, setLeaderboard] = useState(null);
  const [topicSuggestions, setTopicSuggestions] = useState([]);
  const [loadingTopics, setLoadingTopics] = useState(false);
  const [showTopicDialog, setShowTopicDialog] = useState(false);
  const [userSearchOpen, setUserSearchOpen] = useState(false);
  const [deptSearchOpen, setDeptSearchOpen] = useState(false);
  const currentMonth = new Date().toISOString().slice(0, 7);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedUser && viewMode === "user") {
      fetchUserSessions();
    }
  }, [selectedUser, viewMode]);

  useEffect(() => {
    if (selectedDepartment && viewMode === "department") {
      fetchDepartmentSessions();
    }
  }, [selectedDepartment, viewMode]);

  const fetchData = async () => {
    try {
      const [usersRes, sessionsRes, leaderboardRes] = await Promise.all([
        axios.get(`${API}/users`),
        axios.get(`${API}/sessions`),
        axios.get(`${API}/analytics/leaderboard`)
      ]);
      
      setUsers(usersRes.data);
      setAllSessions(sessionsRes.data);
      setLeaderboard(leaderboardRes.data);
      
      // Extract unique departments
      const depts = [...new Set(usersRes.data.map(u => u.department))].filter(Boolean);
      setDepartments(depts);
      
      if (usersRes.data.length > 0) {
        setSelectedUser(usersRes.data[0].id);
      }
      if (depts.length > 0) {
        setSelectedDepartment(depts[0]);
      }
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserSessions = async () => {
    const userSessions = allSessions.filter(s => s.user_id === selectedUser);
    setSessions(userSessions);
  };

  const fetchDepartmentSessions = async () => {
    const deptUsers = users.filter(u => u.department === selectedDepartment);
    const deptUserIds = deptUsers.map(u => u.id);
    const deptSessions = allSessions.filter(s => deptUserIds.includes(s.user_id));
    setSessions(deptSessions);
  };

  const generateTopicIdeas = async () => {
    setLoadingTopics(true);
    setShowTopicDialog(true);
    try {
      const currentUser = users.find(u => u.id === selectedUser);
      const response = await axios.post(`${API}/ai/suggest-topics`, {
        industry: currentUser?.department || selectedDepartment || "General",
        role: "Professional",
        skill_focus: "Presentation and communication skills",
        difficulty: "intermediate"
      });
      setTopicSuggestions(response.data.topics || []);
    } catch (error) {
      console.error("Failed to generate topics:", error);
      toast.error("Failed to generate topic ideas");
    } finally {
      setLoadingTopics(false);
    }
  };

  const getMedalColor = (index) => {
    switch(index) {
      case 0: return "text-[#FFD700]";
      case 1: return "text-[#C0C0C0]";
      case 2: return "text-[#CD7F32]";
      default: return "text-slate-400";
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'analyzed': return 'Completed';
      case 'completed': return 'Awaiting Analysis';
      case 'in_progress': return 'In Progress';
      default: return 'Ready';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'analyzed': return 'bg-emerald-500';
      case 'completed': return 'bg-blue-500';
      case 'in_progress': return 'bg-amber-500';
      default: return 'bg-slate-400';
    }
  };

  const currentUser = users.find(u => u.id === selectedUser);
  const deptUsers = users.filter(u => u.department === selectedDepartment);
  const deptTopPerformers = [...deptUsers].sort((a, b) => (b.average_score || 0) - (a.average_score || 0));
  
  const thisMonthSessions = sessions.filter(s => s.month === currentMonth);
  const hasCompletedThisMonth = thisMonthSessions.some(s => s.status === 'analyzed' || s.status === 'completed');
  const filteredSessions = sessions.filter(session =>
    session.topic_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const userRank = leaderboard?.top_by_score?.findIndex(u => u.id === selectedUser) + 1 || 0;

  // Department stats
  const deptAvgScore = deptUsers.length > 0 
    ? Math.round(deptUsers.reduce((sum, u) => sum + (u.average_score || 0), 0) / deptUsers.length)
    : 0;
  const deptTotalSessions = sessions.length;
  const deptCompletedSessions = sessions.filter(s => s.status === 'analyzed' || s.status === 'completed').length;
  const deptCompliantUsers = deptUsers.filter(u => 
    allSessions.some(s => s.user_id === u.id && s.month === currentMonth && (s.status === 'analyzed' || s.status === 'completed'))
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header with Tabs and Dropdowns */}
      <div className="flex flex-col gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            My Sessions
          </h1>
          <p className="text-slate-500 mt-1">View and manage sessions by user or department</p>
        </div>

        {/* View Mode Tabs and Selectors */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <Tabs value={viewMode} onValueChange={setViewMode} className="w-full sm:w-auto">
            <TabsList className="grid w-full sm:w-auto grid-cols-2">
              <TabsTrigger value="user" className="flex items-center gap-2" data-testid="tab-user">
                <User className="w-4 h-4" />
                By User
              </TabsTrigger>
              <TabsTrigger value="department" className="flex items-center gap-2" data-testid="tab-department">
                <Building className="w-4 h-4" />
                By Department
              </TabsTrigger>
            </TabsList>
          </Tabs>

          {viewMode === "user" ? (
            <Popover open={userSearchOpen} onOpenChange={setUserSearchOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={userSearchOpen}
                  data-testid="user-select"
                  className="w-full sm:w-72 justify-between"
                >
                  {selectedUser ? (
                    <div className="flex items-center gap-2">
                      <Avatar className="w-6 h-6">
                        <AvatarFallback className="bg-primary text-white text-xs">
                          {currentUser?.name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                        </AvatarFallback>
                      </Avatar>
                      <span>{currentUser?.name}</span>
                      <span className="text-xs text-slate-400">({currentUser?.department})</span>
                    </div>
                  ) : (
                    "Select user..."
                  )}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-72 p-0">
                <Command>
                  <CommandInput placeholder="Search user..." />
                  <CommandList>
                    <CommandEmpty>No user found.</CommandEmpty>
                    <CommandGroup>
                      {users.map((user) => (
                        <CommandItem
                          key={user.id}
                          value={user.name}
                          onSelect={() => {
                            setSelectedUser(user.id);
                            setUserSearchOpen(false);
                          }}
                        >
                          <Check className={`mr-2 h-4 w-4 ${selectedUser === user.id ? "opacity-100" : "opacity-0"}`} />
                          <Avatar className="w-6 h-6 mr-2">
                            <AvatarFallback className="bg-primary text-white text-xs">
                              {user.name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                            </AvatarFallback>
                          </Avatar>
                          <span>{user.name}</span>
                          <span className="text-xs text-slate-400 ml-2">({user.department})</span>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          ) : (
            <Popover open={deptSearchOpen} onOpenChange={setDeptSearchOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={deptSearchOpen}
                  data-testid="department-select"
                  className="w-full sm:w-72 justify-between"
                >
                  {selectedDepartment ? (
                    <div className="flex items-center gap-2">
                      <Building className="w-4 h-4 text-primary" />
                      <span>{selectedDepartment}</span>
                      <span className="text-xs text-slate-400">
                        ({users.filter(u => u.department === selectedDepartment).length} users)
                      </span>
                    </div>
                  ) : (
                    "Select department..."
                  )}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-72 p-0">
                <Command>
                  <CommandInput placeholder="Search department..." />
                  <CommandList>
                    <CommandEmpty>No department found.</CommandEmpty>
                    <CommandGroup>
                      {departments.map((dept) => (
                        <CommandItem
                          key={dept}
                          value={dept}
                          onSelect={() => {
                            setSelectedDepartment(dept);
                            setDeptSearchOpen(false);
                          }}
                        >
                          <Check className={`mr-2 h-4 w-4 ${selectedDepartment === dept ? "opacity-100" : "opacity-0"}`} />
                          <Building className="w-4 h-4 mr-2 text-primary" />
                          <span>{dept}</span>
                          <span className="text-xs text-slate-400 ml-2">
                            ({users.filter(u => u.department === dept).length} users)
                          </span>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          )}
        </div>
      </div>

      {/* User View */}
      {viewMode === "user" && currentUser && (
        <>
          {/* User Hero Card */}
          <Card className="overflow-hidden border-0 shadow-xl bg-gradient-to-br from-primary via-primary to-teal-700">
            <CardContent className="p-8">
              <div className="flex items-start gap-6">
                <Avatar className="w-20 h-20 border-4 border-white/20 shadow-2xl">
                  <AvatarFallback className="bg-white/20 text-white text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>
                    {currentUser.name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <p className="text-white/70 text-sm uppercase tracking-widest font-semibold mb-1">User Profile</p>
                  <h2 className="text-3xl font-bold text-white mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {currentUser.name}
                  </h2>
                  <p className="text-white/80">{currentUser.department} • {currentUser.email}</p>
                  
                  <div className="flex items-center gap-6 mt-6">
                    <div className="text-center">
                      <p className="text-4xl font-bold text-white" style={{ fontFamily: 'Manrope' }}>{currentUser.average_score || 0}</p>
                      <p className="text-white/70 text-sm">Avg Score</p>
                    </div>
                    <div className="w-px h-12 bg-white/20" />
                    <div className="text-center">
                      <p className="text-4xl font-bold text-white" style={{ fontFamily: 'Manrope' }}>{currentUser.sessions_completed || 0}</p>
                      <p className="text-white/70 text-sm">Sessions</p>
                    </div>
                    <div className="w-px h-12 bg-white/20" />
                    <div className="text-center">
                      <p className="text-4xl font-bold text-white" style={{ fontFamily: 'Manrope' }}>#{userRank || '-'}</p>
                      <p className="text-white/70 text-sm">Rank</p>
                    </div>
                    <div className="w-px h-12 bg-white/20" />
                    <div className="text-center">
                      <div className={`px-3 py-1 rounded-full text-sm font-semibold ${
                        hasCompletedThisMonth ? 'bg-emerald-500 text-white' : 'bg-red-500 text-white'
                      }`}>
                        {hasCompletedThisMonth ? 'Compliant' : 'Pending'}
                      </div>
                      <p className="text-white/70 text-sm mt-1">This Month</p>
                    </div>
                  </div>
                </div>

                {/* Score Ring */}
                <div className="hidden md:flex flex-col items-center">
                  <div className="relative w-28 h-28">
                    <svg className="w-full h-full transform -rotate-90">
                      <circle cx="56" cy="56" r="48" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="10" />
                      <circle 
                        cx="56" cy="56" r="48" fill="none" stroke="white" strokeWidth="10"
                        strokeDasharray={`${(currentUser.average_score || 0) * 3.02} 302`}
                        strokeLinecap="round"
                      />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-xl font-bold text-white">{currentUser.average_score || 0}%</span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* User Stats Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { icon: Target, label: "Total Sessions", value: currentUser.sessions_completed || 0, color: "text-primary" },
              { icon: Award, label: "Average Score", value: currentUser.average_score || 0, color: "text-amber-500" },
              { icon: Star, label: "Excellent Sessions", value: sessions.filter(s => s.overall_score >= 80).length, color: "text-emerald-500" },
              { icon: Calendar, label: "This Month", value: thisMonthSessions.length, color: "text-blue-500" }
            ].map((stat, i) => (
              <Card key={i} className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                <CardContent className="p-5 text-center">
                  <stat.icon className={`w-7 h-7 mx-auto mb-2 ${stat.color}`} />
                  <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>{stat.value}</p>
                  <p className="text-xs text-slate-500">{stat.label}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </>
      )}

      {/* Department View */}
      {viewMode === "department" && selectedDepartment && (
        <>
          {/* Department Hero Card */}
          <Card className="overflow-hidden border-0 shadow-xl bg-gradient-to-br from-indigo-600 via-purple-600 to-purple-700">
            <CardContent className="p-8">
              <div className="flex items-start gap-6">
                <div className="w-20 h-20 rounded-2xl bg-white/20 flex items-center justify-center">
                  <Building className="w-10 h-10 text-white" />
                </div>
                <div className="flex-1">
                  <p className="text-white/70 text-sm uppercase tracking-widest font-semibold mb-1">Department</p>
                  <h2 className="text-3xl font-bold text-white mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {selectedDepartment}
                  </h2>
                  <p className="text-white/80">{deptUsers.length} team members</p>
                  
                  <div className="flex items-center gap-6 mt-6">
                    <div className="text-center">
                      <p className="text-4xl font-bold text-white" style={{ fontFamily: 'Manrope' }}>{deptAvgScore}</p>
                      <p className="text-white/70 text-sm">Avg Score</p>
                    </div>
                    <div className="w-px h-12 bg-white/20" />
                    <div className="text-center">
                      <p className="text-4xl font-bold text-white" style={{ fontFamily: 'Manrope' }}>{deptTotalSessions}</p>
                      <p className="text-white/70 text-sm">Total Sessions</p>
                    </div>
                    <div className="w-px h-12 bg-white/20" />
                    <div className="text-center">
                      <p className="text-4xl font-bold text-white" style={{ fontFamily: 'Manrope' }}>{deptCompletedSessions}</p>
                      <p className="text-white/70 text-sm">Completed</p>
                    </div>
                    <div className="w-px h-12 bg-white/20" />
                    <div className="text-center">
                      <p className="text-4xl font-bold text-white" style={{ fontFamily: 'Manrope' }}>{deptCompliantUsers.length}/{deptUsers.length}</p>
                      <p className="text-white/70 text-sm">Compliant</p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Department Stats Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { icon: Users, label: "Team Members", value: deptUsers.length, color: "text-primary" },
              { icon: Award, label: "Avg Score", value: deptAvgScore, color: "text-amber-500" },
              { icon: Presentation, label: "Total Sessions", value: deptTotalSessions, color: "text-emerald-500" },
              { icon: CheckCircle, label: "Compliance Rate", value: `${deptUsers.length > 0 ? Math.round(deptCompliantUsers.length / deptUsers.length * 100) : 0}%`, color: "text-blue-500" }
            ].map((stat, i) => (
              <Card key={i} className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                <CardContent className="p-5 text-center">
                  <stat.icon className={`w-7 h-7 mx-auto mb-2 ${stat.color}`} />
                  <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>{stat.value}</p>
                  <p className="text-xs text-slate-500">{stat.label}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Department Top Performers */}
          <Card className="border-0 shadow-xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                <Trophy className="w-5 h-5 text-amber-500" />
                Department Top Performers
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {deptTopPerformers.slice(0, 6).map((user, index) => (
                  <div 
                    key={user.id}
                    onClick={() => { setSelectedUser(user.id); setViewMode("user"); }}
                    className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 hover:bg-slate-100 cursor-pointer transition-colors"
                  >
                    <div className="w-8 h-8 flex items-center justify-center">
                      {index < 3 ? (
                        <Medal className={`w-6 h-6 ${getMedalColor(index)}`} />
                      ) : (
                        <span className="text-sm font-bold text-slate-400">#{index + 1}</span>
                      )}
                    </div>
                    <Avatar className="w-10 h-10">
                      <AvatarFallback className="bg-primary text-white text-sm font-semibold">
                        {user.name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-slate-900 truncate text-sm">{user.name}</p>
                      <p className="text-xs text-slate-500">{user.sessions_completed || 0} sessions</p>
                    </div>
                    <p className="font-bold text-primary text-lg">{user.average_score || 0}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Sessions and Leaderboard Grid */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Sessions List */}
        <div className="lg:col-span-2">
          <Card className="border-0 shadow-xl">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                  <Presentation className="w-5 h-5 text-primary" />
                  {viewMode === "user" ? "Session History" : "Department Sessions"}
                </CardTitle>
                <div className="relative w-48">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 rounded-full bg-slate-50 border-0"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {filteredSessions.length > 0 ? (
                <div className="space-y-3">
                  {filteredSessions.slice(0, 8).map((session) => {
                    const sessionUser = users.find(u => u.id === session.user_id);
                    return (
                      <div 
                        key={session.id}
                        className="group flex items-center gap-4 p-4 rounded-xl bg-slate-50 hover:bg-slate-100 transition-all"
                      >
                        <div className={`w-2 h-14 rounded-full ${getStatusColor(session.status)}`} />
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-slate-900 truncate">{session.topic_name}</p>
                          {viewMode === "department" && (
                            <p className="text-sm text-slate-500">by {sessionUser?.name}</p>
                          )}
                          <p className="text-xs text-slate-400 mt-1">
                            {new Date(session.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </p>
                        </div>
                        <span className="text-xs px-3 py-1 rounded-full bg-slate-200 text-slate-600">
                          {getStatusLabel(session.status)}
                        </span>
                        {session.overall_score && (
                          <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-white ${
                            session.overall_score >= 80 ? 'bg-emerald-500' :
                            session.overall_score >= 60 ? 'bg-amber-500' : 'bg-red-500'
                          }`}>
                            {session.overall_score}
                          </div>
                        )}
                        <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          {session.status === 'created' && (
                            <Link to={`/session/${session.id}/live`}>
                              <Button size="sm" className="rounded-full bg-primary hover:bg-primary/90">
                                <Play className="w-4 h-4" />
                              </Button>
                            </Link>
                          )}
                          {(session.status === 'analyzed' || session.status === 'completed') && (
                            <>
                              <Link to={`/session/${session.id}/replay`}>
                                <Button size="sm" variant="outline" className="rounded-full">
                                  <Play className="w-4 h-4" />
                                </Button>
                              </Link>
                              {session.status === 'analyzed' && (
                                <Link to={`/session/${session.id}/feedback`}>
                                  <Button size="sm" variant="outline" className="rounded-full">
                                    <Eye className="w-4 h-4" />
                                  </Button>
                                </Link>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="py-12 text-center">
                  <Presentation className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No sessions found</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right Sidebar */}
        <div className="space-y-6">
          {/* Leaderboard */}
          <Card className="border-0 shadow-xl">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-bold flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                <Trophy className="w-5 h-5 text-amber-500" />
                Top Performers
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {leaderboard?.top_by_score?.slice(0, 5).map((user, index) => (
                <div 
                  key={user.id}
                  onClick={() => { setSelectedUser(user.id); setViewMode("user"); }}
                  className={`flex items-center gap-3 p-3 rounded-xl transition-all cursor-pointer ${
                    user.id === selectedUser && viewMode === "user" ? 'bg-primary/10 ring-1 ring-primary/20' : 'hover:bg-slate-50'
                  }`}
                >
                  <div className="w-8 h-8 flex items-center justify-center">
                    {index < 3 ? (
                      <Medal className={`w-6 h-6 ${getMedalColor(index)}`} />
                    ) : (
                      <span className="text-sm font-bold text-slate-400">#{index + 1}</span>
                    )}
                  </div>
                  <Avatar className="w-9 h-9">
                    <AvatarFallback className="bg-primary text-white text-xs font-semibold">
                      {user.name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-900 truncate text-sm">{user.name}</p>
                    <p className="text-xs text-slate-500">{user.sessions_completed || 0} sessions</p>
                  </div>
                  <p className="font-bold text-primary">{user.average_score || 0}</p>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* AI Topic Generator */}
          <Card className="border-0 shadow-xl bg-gradient-to-br from-amber-50 to-orange-50 overflow-hidden relative">
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-amber-400/20 to-orange-400/20 rounded-full blur-3xl" />
            <CardContent className="p-5 relative z-10">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center mb-3 shadow-lg shadow-amber-500/30">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-1" style={{ fontFamily: 'Manrope' }}>
                Need Ideas?
              </h3>
              <p className="text-slate-600 text-sm mb-4">
                Let AI suggest presentation topics
              </p>
              <Dialog open={showTopicDialog} onOpenChange={setShowTopicDialog}>
                <DialogTrigger asChild>
                  <Button 
                    onClick={generateTopicIdeas}
                    data-testid="generate-topics-btn"
                    className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 rounded-full shadow-lg"
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    Generate Ideas
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                      <Sparkles className="w-5 h-5 text-amber-500" />
                      AI-Generated Topic Ideas
                    </DialogTitle>
                  </DialogHeader>
                  {loadingTopics ? (
                    <div className="py-12 text-center">
                      <Loader2 className="w-12 h-12 text-amber-500 animate-spin mx-auto mb-4" />
                      <p className="text-slate-600">Generating ideas...</p>
                    </div>
                  ) : (
                    <div className="space-y-4 mt-4">
                      {topicSuggestions.map((topic, index) => (
                        <div key={index} className="p-4 bg-slate-50 rounded-2xl border border-slate-200">
                          <div className="flex items-center gap-2 mb-2">
                            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-sm">
                              {index + 1}
                            </div>
                            <h4 className="font-bold text-slate-900">{topic.title}</h4>
                          </div>
                          <p className="text-sm text-slate-600 mb-3">{topic.description}</p>
                          <div className="mb-3">
                            <p className="text-xs font-semibold text-slate-700 mb-2">📌 Key Points:</p>
                            <ul className="space-y-1 pl-4">
                              {topic.key_points?.map((point, i) => (
                                <li key={i} className="text-sm text-slate-600 list-disc">{point}</li>
                              ))}
                            </ul>
                          </div>
                          <div className="flex items-center gap-4 text-xs text-slate-500 pt-2 border-t">
                            <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{topic.duration_minutes || 10} mins</span>
                            <span>{topic.importance}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </DialogContent>
              </Dialog>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
